import { PayloadAction, createAction, createSlice } from "@reduxjs/toolkit";
import { Query, QueryResult, TreemapGroup } from "../../types/app";

type AddFile = {
  fileId: string;
  fileName: string;
};

export interface SearchState {
  searchType: "default" | "speed" | "accuracy";
  resultsToDisplay: number;
  queryLoading: boolean;
  feedbackDialogOpen: boolean;
  configDialogOpen: boolean;
  searchQuery: string;
  treemapGroups: TreemapGroup;
  queryResult: QueryResult[];
  prevQueryResult: QueryResult[];
  queries: Query[];
  clusters: string[];
  filesToSearch: { [key: string]: string };
}

const initialState: SearchState = {
  searchType: "accuracy",
  resultsToDisplay: 30,
  queryLoading: false,
  feedbackDialogOpen: false,
  configDialogOpen: false,
  searchQuery: "",
  queryResult: [],
  prevQueryResult: [],
  queries: [],
  clusters: [],
  treemapGroups: {} as TreemapGroup,
  filesToSearch: {},
};

export const searchResetAll = createAction("RESET_ALL");

export const searchSlice = createSlice({
  name: "counter",
  initialState,
  reducers: {
    setSearchType: (
      state,
      action: PayloadAction<"default" | "speed" | "accuracy">
    ) => {
      state.searchType = action.payload;
    },
    setResultsToDisplay: (state, action: PayloadAction<number>) => {
      state.resultsToDisplay = action.payload;
    },
    setQueryLoadingState: (state, action: PayloadAction<boolean>) => {
      state.queryLoading = action.payload;
    },
    setFeedbackDialogState: (state) => {
      state.feedbackDialogOpen = !state.feedbackDialogOpen;
    },
    setConfigDialogState: (state) => {
      state.configDialogOpen = !state.configDialogOpen;
    },
    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.searchQuery = action.payload;
    },
    setQueryResult: (state, action: PayloadAction<QueryResult[]>) => {
      state.queryResult = action.payload;
    },
    setPrevQueryResult: (state) => {
      state.prevQueryResult = state.queryResult;
    },
    setQueries: (state, action: PayloadAction<Query[]>) => {
      state.queries = action.payload;
    },
    setClusters: (state, action: PayloadAction<string[]>) => {
      state.clusters = action.payload;
    },
    setTreemapGroups: (state, action: PayloadAction<TreemapGroup>) => {
      state.treemapGroups = action.payload;
    },
    addFilesToSearch(state, action: PayloadAction<AddFile>) {
      const newFilesToSearch = { ...state.filesToSearch };
      newFilesToSearch[action.payload.fileName] = action.payload.fileId;
      state.filesToSearch = newFilesToSearch;
    },
    deleteFilesFromSearch(state, action: PayloadAction<string>) {
      const newFilesToSearch = { ...state.filesToSearch };
      delete newFilesToSearch[action.payload];
      state.filesToSearch = newFilesToSearch;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(searchResetAll, () => initialState);
  },
});

export const {
  setSearchType,
  setResultsToDisplay,
  setQueryLoadingState,
  setFeedbackDialogState,
  setConfigDialogState,
  setSearchQuery,
  setQueryResult,
  setPrevQueryResult,
  setQueries,
  setClusters,
  setTreemapGroups,
  addFilesToSearch,
  deleteFilesFromSearch,
} = searchSlice.actions;

export default searchSlice.reducer;
